// src/pages/StockProfileAnalysis.js
import React, { useEffect, useLayoutEffect, useState, useRef, useCallback } from "react";

import {
  Box,
  Flex,
  Input,
  Button,
  IconButton,
  VStack,
  Text,
  Heading,
  Spinner,
  Divider,
  Progress,
  HStack,
  Badge,
  CircularProgress,
  CircularProgressLabel,
  SimpleGrid,
  Card,
  CardHeader,
  CardBody,
  Stack,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { AddIcon } from "@chakra-ui/icons";
import { useNavigate } from "react-router-dom";

import usePriceData from "../hooks/usePriceData";
import useWatchlist from "../hooks/useWatchlist";
import useSearchTickers from "../hooks/useSearchTickers";
import WatchlistSidebar from "../components/WatchlistSidebar";
import apiClient from "../api/apiClient";

import { Drawer, DrawerOverlay, DrawerContent, DrawerHeader, DrawerBody } from "@chakra-ui/react";

import {
  ReferenceArea,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Layer,
} from "recharts";

const StockProfileAnalysis = () => {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const searchInputRef = useRef(null);
  const chartContainerRef = useRef(null);

  const [hoveredTicker, setHoveredTicker] = useState(null);
  const [searchInput, setSearchInput] = useState("");

  const [selectedTicker, setSelectedTicker] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [scoreData, setScoreData] = useState(null);
  const [loadingChart, setLoadingChart] = useState(false);
  const [loadingTicker, setLoadingTicker] = useState(false);
  const [tooltipData, setTooltipData] = useState(null);
  const [chartDims, setChartDims] = useState({ width: 0, height: 0 });

  const [llmCache, setLlmCache] = useState({}); // cache for LLM outputs

  const navigate = useNavigate();
  const { watchlist, loading: loadingWatchlist, addTicker, removeTicker } = useWatchlist(navigate);
  const searchSuggestions = useSearchTickers(searchInput);
  const { priceData, lastUpdated } = usePriceData(watchlist);

  const isMobile = () => window.innerWidth < 768;

  // =============================
  // Fetch chart data (safe)
  // =============================

  const fetchChartData = useCallback(async () => {
    if (!watchlist.length) {
      setChartData([]);
      return;
    }

    setLoadingChart(true);

    const validTickers = watchlist.filter(
      (t) => t && typeof t === "string" && t.trim() !== ""
    );
    if (!validTickers.length) {
      setChartData([]);
      return;
    }

    const jitter = (val) => {
      if (val === null || val === undefined || isNaN(val)) return 0;

      const j = (Math.random() - 0.5) * 2; // ±1
      let result = val + j;

      // Clamp the value between 0 and 100
      result = Math.max(0, Math.min(100, result));

      return parseFloat(result.toFixed(2));
    };


    const fetchSingleTicker = async (ticker, retries = 2) => {
      try {
        const res = await apiClient.post("/stock/scores", { tickers: [ticker] });
        const data = res.data?.[0];
        if (!data?.llmResult) throw new Error("Invalid LLM result");

        return {
          ticker,
          category: data.llmResult.category ?? "N/A",
          growth_score: jitter(Number(data.llmResult.growth_score ?? 0)),
          risk_score: jitter(Number(data.llmResult.risk_score ?? 0)),
          valuation_score: jitter(Number(data.llmResult.valuation_score ?? 0)),
          overall_score: jitter(Number(data.llmResult.overall_score ?? 0)),
          rationale: data.llmResult.rationale ?? { summary: "", dimensions: [] },
          status: "ok",
        };
      } catch (err) {
        if (retries > 0) {
          console.warn(`Retrying ${ticker}... attempts left: ${retries}`);
          await new Promise((r) => setTimeout(r, 800));
          return fetchSingleTicker(ticker, retries - 1);
        }
        console.warn(`⚠️ Skipping ${ticker}: ${err.message}`);
        // return placeholder instead of null
        return {
          ticker,
          category: "N/A",
          growth_score: 0,
          risk_score: 0,
          valuation_score: 0,
          overall_score: 0,
          rationale: { summary: "Data unavailable", dimensions: [] },
          status: "failed",
        };
      }
    };

    try {
      const results = await Promise.all(validTickers.map(fetchSingleTicker));
      const hasValid = results.some((r) => r.status === "ok");

      if (!hasValid) {
        toast({
          title: "No stock scores could be loaded",
          status: "warning",
          duration: 3000,
          isClosable: true,
        });
      }

      setChartData(results);
    } catch (err) {
      console.error("Unexpected error loading chart data:", err);
    } finally {
      setLoadingChart(false);
    }
  }, [watchlist, toast]);




  useEffect(() => {
    fetchChartData();
  }, [fetchChartData]);

  // =============================
  // Fetch LLM evaluation once per ticker
  // =============================
  const handleDotClick = async (ticker) => {
    setSelectedTicker(ticker);

    // If already cached, use it
    if (llmCache[ticker]) {
      setScoreData(llmCache[ticker]);
      return;
    }

    setLoadingTicker(true);
    try {
      const token = localStorage.getItem("token");
      const res = await apiClient.get(`/stock/score/${ticker}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setScoreData(res.data?.llmResult);
      setLlmCache((prev) => ({ ...prev, [ticker]: res.data?.llmResult }));
    } catch (err) {
      console.error("❌ Error fetching LLM evaluation:", err);
      toast({
        title: "Failed to load evaluation",
        description: err.response?.data?.error || err.message,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setLoadingTicker(false);
    }
  };

  // =============================
  // ScoreData sync when selectedTicker changes
  // =============================
  useEffect(() => {
    if (!selectedTicker) return setScoreData(null);
    if (llmCache[selectedTicker]) setScoreData(llmCache[selectedTicker]);
  }, [selectedTicker, llmCache]);

  // =============================
  // Chart hover tooltip
  // =============================
  const handleHoverTicker = (ticker) => setHoveredTicker(ticker);
  const handleLeaveHover = () => setHoveredTicker(null);

  useEffect(() => {
    const activeTicker = hoveredTicker || selectedTicker;
    if (!activeTicker || chartData.length === 0 || !chartContainerRef.current) {
      setTooltipData(null);
      return;
    }

    const found = chartData.find((d) => d.ticker === activeTicker);
    if (!found) {
      setTooltipData(null);
      return;
    }

    const { clientWidth, clientHeight } = chartContainerRef.current;
    const x = (found.growth_score / 100) * clientWidth;
    const y = clientHeight - (found.risk_score / 100) * clientHeight;

    setTooltipData({
      active: true,
      payload: [{ payload: found }],
      label: found.ticker,
      coordinate: { x, y },
    });
  }, [hoveredTicker, selectedTicker, chartData]);

  // =============================
  // Add ticker
  // =============================
  const handleAddTicker = (ticker) => {
    addTicker(ticker);
    if (isMobile()) onClose();
  };

  useLayoutEffect(() => {
    if (chartContainerRef.current) {
      const { clientWidth, clientHeight } = chartContainerRef.current;
      setChartDims({ width: clientWidth, height: clientHeight });
    }
  }, [chartData]);

  const activeTicker = selectedTicker || scoreData?.ticker;
  const activeStock = chartData.find((s) => s.ticker === activeTicker);

  // =============================
  // Render
  // =============================
  // Listen for global watchlist selection events (floating button / layout drawer)
  React.useEffect(() => {
    const handler = (e) => {
      const ticker = e.detail;
      if (ticker) {
        handleDotClick(ticker);
        if (onClose) onClose();
      }
    };
    window.addEventListener('watchlist-select', handler);
    return () => window.removeEventListener('watchlist-select', handler);
  }, [handleDotClick, onClose]);

  return (
    <Flex direction={{ base: "column", md: "row" }} h="100vh">

      {/* Mobile Drawer */}
      <Drawer isOpen={isOpen} placement="left" onClose={onClose}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerHeader>Watchlist</DrawerHeader>
          <DrawerBody>
            <WatchlistSidebar
              watchlist={watchlist}
              priceData={priceData}
              selectedTicker={selectedTicker}
              onSelectTicker={setSelectedTicker}
              onRemoveTicker={removeTicker}
              loading={loadingWatchlist}
              lastUpdated={lastUpdated}
              onClose={onClose}
            />
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* Desktop Sidebar */}
      <Box flex="0 0 230px" overflowY="auto" display={{ base: "none", md: "block" }}>
        <WatchlistSidebar
          watchlist={watchlist}
          priceData={priceData}
          selectedTicker={selectedTicker}
          onSelectTicker={setSelectedTicker}
          onRemoveTicker={removeTicker}
          loading={loadingWatchlist}
          lastUpdated={lastUpdated}
        />
      </Box>

      {/* Main Content */}
      <Flex flex="1" direction="column" px={0} py={6} overflow="hidden">
        <Box flex="1" overflowY="auto" p={0}>
          {/* Search Box */}
          <Box p={4} bg="white" borderBottom="1px solid" borderColor="gray.200">
            <Box position="relative" mb={2}>
              <Input
                ref={searchInputRef}
                placeholder="Search ticker..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
              {searchSuggestions.length > 0 && (
                <Box position="absolute" top="100%" left="0" right="0" bg="white" shadow="md" borderRadius="md" zIndex={10}>
                  <VStack spacing={0}>
                    {searchSuggestions.map((item) => (
                      <Flex
                        key={item.ticker}
                        justify="space-between"
                        align="center"
                        w="full"
                        px={3}
                        py={2}
                        _hover={{ bg: "gray.50" }}
                      >
                        <Button
                          variant="link"
                          colorScheme="teal"
                          onClick={() => {
                            setSelectedTicker(item.ticker);
                            setSearchInput("");
                          }}
                        >
                          {item.ticker} - {item.name}
                        </Button>
                        <IconButton
                          icon={<AddIcon />}
                          size="xs"
                          colorScheme="green"
                          aria-label="Add"
                          onClick={() => {
                            handleAddTicker(item.ticker);
                            setSearchInput("");          // Clear search input

                          }}
                        />
                      </Flex>
                    ))}
                  </VStack>
                </Box>
              )}
            </Box>
          </Box>


          <Divider mb={4} />


          {/* Loading Chart Spinner */}
          {loadingChart && (
            <Flex align="center" justify="center" mb={4}>
              <Spinner size="lg" />
              <Text ml={2}>Loading chart...</Text>
            </Flex>
          )}

          {/* Chart */}
          {/* Chart Container */}
          <Box
            ref={chartContainerRef}
            w="100%"
            h={isMobile() ? "400px" : "500px"}
            mb={6}
            bg="white"
            borderRadius="lg"
            border="1px solid"
            borderColor="gray.200"
            p={isMobile() ? 2 : 4}
          >
            <ResponsiveContainer width="100%" height="100%">
              <ScatterChart
                margin={{
                  top: isMobile() ? 40 : 40,
                  right: isMobile() ? 0 : 0,
                  bottom: isMobile() ? 40 : 40,
                  left: isMobile() ? 0 : 0,
                }}
              >
                {/* Quadrant Backgrounds */}
                <ReferenceArea x1={0} y1={50} x2={50} y2={100} fill="#ffecec" fillOpacity={0.4} />
                <ReferenceArea x1={50} y1={50} x2={100} y2={100} fill="#fff5e6" fillOpacity={0.4} />
                <ReferenceArea x1={0} y1={0} x2={50} y2={50} fill="#e6f0ff" fillOpacity={0.4} />
                <ReferenceArea x1={50} y1={0} x2={100} y2={50} fill="#e8fff0" fillOpacity={0.4} />

                {/* Quadrant Titles */}
                {chartDims.width > 0 && chartDims.height > 0 && (
                  <Layer>
                    <text
                      x={chartDims.width * 1.35 / 4}
                      y={chartDims.height / 4}
                      textAnchor="middle"
                      fontSize={isMobile() ? 10 : 12}
                      fontWeight="600"
                      fill="#800000"
                    >
                      Avoid/Speculative
                    </text>
                    <text
                      x={(chartDims.width * 3) / 4}
                      y={chartDims.height / 4}
                      textAnchor="middle"
                      fontSize={isMobile() ? 10 : 12}
                      fontWeight="600"
                      fill="#cc6600"
                    >
                      Dynamic Risers
                    </text>
                    <text
                      x={chartDims.width * 1.35 / 4}
                      y={(chartDims.height * 2.5) / 4}
                      textAnchor="middle"
                      fontSize={isMobile() ? 10 : 12}
                      fontWeight="600"
                      fill="#003366"
                    >
                      Steady Defenders
                    </text>
                    <text
                      x={(chartDims.width * 3) / 4}
                      y={(chartDims.height * 2.5) / 4}
                      textAnchor="middle"
                      fontSize={isMobile() ? 10 : 12}
                      fontWeight="600"
                      fill="#006600"
                    >
                      Prime Performers
                    </text>
                  </Layer>
                )}

                <CartesianGrid strokeDasharray="3 3" />

                {/* Remove numeric ticks and axis lines on mobile */}
                <XAxis
                  type="number"
                  dataKey="growth_score"
                  domain={[0, 100]}
                  tick={true}
                  fontSize={7}
                  label={
                    {
                      value: "Growth / Expected Return",
                      position: "bottom",
                      offset: 10,
                      fontSize: 12,
                      fontWeight: "800",
                    }

                  }
                />
                <YAxis
                  type="number"
                  dataKey="risk_score"
                  domain={[0, 100]}
                  tick={true}
                  fontSize={7}
                  label={{

                    value: "Risk",
                    angle: -90,
                    position: "leftside",
                    fontSize: 12,
                    fontWeight: "800",
                  }

                  }
                />

                <Tooltip
                  cursor={{ strokeDasharray: "3 3" }}
                  content={({ active, payload }) => {
                    if (active && payload?.length) {
                      const data = payload[0].payload;
                      return (
                        <Box p={2} bg="white" border="1px solid #ccc" borderRadius="md" shadow="md">
                          <Text fontWeight="bold">{data.ticker}</Text>
                          <Text>Growth: {data.growth_score}</Text>
                          <Text>Risk: {data.risk_score}</Text>
                          {/* <Text>Valuation: {data.valuation_score}</Text> */}
                          <Text>Overall Score: {data.overall_score}</Text>
                        </Box>
                      );
                    }
                    return null;
                  }}
                />

                {/* Scatter dots */}
                <Scatter
                  data={chartData}
                  shape={({ cx, cy, payload }) => {
                    if (cx == null || cy == null) return null;

                    const t = payload.ticker;
                    const isSelected = t === selectedTicker;
                    const isHovered = t === hoveredTicker;
                    const radius = isSelected || isHovered ? 9 : 6;

                    const valuation = payload.valuation_score ?? 0;
                    const baseColor =
                      valuation < 50 ? "#E74C3C" : valuation < 75 ? "#F1C40F" : "#2ECC71";

                    const fillColor =
                      payload.status === "failed"
                        ? "#A0AEC0"
                        : isSelected
                          ? "#E53E3E"
                          : isHovered
                            ? "#ECC94B"
                            : baseColor;

                    const strokeColor = isSelected || isHovered ? "#0f172a" : "none";

                    return (
                      <circle
                        cx={cx}
                        cy={cy}
                        r={radius}
                        fill={fillColor}
                        stroke={strokeColor}
                        strokeWidth={isSelected ? 2 : isHovered ? 1.5 : 0}
                        onMouseEnter={() => handleHoverTicker(t)}
                        onMouseLeave={handleLeaveHover}
                        onClick={() => handleDotClick(t)}
                        pointerEvents="all"
                        style={{ cursor: "pointer", transition: "all 0.15s ease" }}
                      />
                    );
                  }}
                  isAnimationActive={false}
                />
              </ScatterChart>
            </ResponsiveContainer>
          </Box>


          <Divider mb={4} />




          {/* Rationale Card Below Chart */}
          {activeStock?.rationale && (


<Card mb={4} boxShadow="md" borderRadius="xl" overflow="hidden">
  <CardHeader bg="gray.100" borderTopRadius="xl" py={3}>
    <HStack justify="space-between" align="start" spacing={4}>
      {/* Left side: Ticker name and summary */}
      <Box flex="1">
        <Heading size="md">{activeStock.ticker}</Heading>
        <Text fontSize="sm" color="gray.700" noOfLines={3}>
          {activeStock?.rationale.summary}
        </Text>
      </Box>

      {/* Right side: Circular progress with score below */}
      <VStack spacing={1} align="center">
        <CircularProgress
          value={activeStock.overall_score}
          size="64px"
          thickness="7px"
          color={
            activeStock.overall_score >= 75
              ? "green.400"
              : activeStock.overall_score >= 50
              ? "yellow.400"
              : "red.400"
          }
        >
          <CircularProgressLabel fontWeight="bold" fontSize="sm">
            {activeStock.overall_score}
          </CircularProgressLabel>
        </CircularProgress>
        <Text fontSize="sm" color="gray.500" mt={0}>
          Overall Score
        </Text>
      </VStack>
    </HStack>
  </CardHeader>

  <CardBody pt={3}>
    {activeStock?.rationale.dimensions.map((d, i) => (
      <Box
        key={i}
        mb={3}
        p={3}
        borderWidth="1px"
        borderColor="gray.200"
        borderRadius="lg"
        bg="gray.50"
      >
        <HStack justify="space-between" mb={1}>
          <Text fontWeight="bold" textTransform="capitalize">
            {d.name.replace(/_/g, " ")}
          </Text>
          <HStack spacing={1}>
            <Badge colorScheme="purple">{(d.weight * 100).toFixed(0)}%</Badge>
            <Badge
              colorScheme={
                d.score >= 75 ? "green" : d.score >= 50 ? "yellow" : "red"
              }
            >
              {d.score}
            </Badge>
          </HStack>
        </HStack>

        <Progress
          value={d.score}
          size="sm"
          colorScheme={
            d.score >= 75 ? "green" : d.score >= 50 ? "yellow" : "red"
          }
          borderRadius="md"
          mb={2}
        />

        <Text fontSize="sm" color="gray.600">
          {d.evidence}
        </Text>
      </Box>
    ))}
  </CardBody>
</Card>


          )}

        </Box>


      </Flex>
    </Flex>
  );
};

export default StockProfileAnalysis;
